import { useFetchMetaTagAutocomplete, useFetchMetaTagList } from '@api';
import { StorageKeyEnum } from '@constant';
import { useMetaTagActions } from '@remote-hooks';
import { useSelector } from '@stores';
import { IMetaTag } from '@types';
import { debounce } from 'lodash';
import { useCallback, useState } from 'react';

// 스탠다드 검색 Output 페이지 및 19금 Output 페이지 태그 검색 로직에 관여
export const useMetaTag = () => {
  const [tagAutocompleteKeyword, setTagAutocompleteKeyword] = useState<string>('');
  const [tagInputKeyword, setTagInputKeyword] = useState<string>('');
  const [isTagging, setIsTagging] = useState(false); // 스페이스 입력 감지 상태

  const selectedMetaTags = useSelector((state) => state.metaTag.selectedMetaTags);
  const aiRecommendedMetaTags = useSelector((state) => state.metaTag.aiRecommendedMetaTags);

  const { onSetMetaTag } = useMetaTagActions();

  const META_TAG_LIST_EXPIRATION_INTERVAL = 86400000; // 1일

  const { data: metaTagAutocompleteData } = useFetchMetaTagAutocomplete(
    tagAutocompleteKeyword && tagAutocompleteKeyword.length > 0 ? tagAutocompleteKeyword : null
  );
  const tagAutocompleteList =
    tagAutocompleteKeyword &&
    tagAutocompleteKeyword.length > 0 &&
    metaTagAutocompleteData &&
    metaTagAutocompleteData.length > 0
      ? metaTagAutocompleteData.map((item) => item.name)
      : [];

  const { refetch: refetchMetaTagList } = useFetchMetaTagList();

  // 브라우저에 캐시된 메타태그 데이터 가져오기
  const loadCachedMetaTagList = (): { metaTagList: string[] | null; expiredDate: number | null } => {
    const cachedData = localStorage.getItem(StorageKeyEnum.ALL_META_TAG_LIST_DATA_KEY);
    if (!cachedData) return { metaTagList: null, expiredDate: null };

    try {
      const parsedData = JSON.parse(cachedData);
      return {
        metaTagList: parsedData.metaTagList || null,
        expiredDate: parsedData.expiredDate || null
      };
    } catch (error) {
      console.error('Failed to parse cached meta tags:', error);
      return { metaTagList: null, expiredDate: null };
    }
  };

  // GET /meta-tags 요청 및 데이터 저장
  const fetchMetaTagList = async () => {
    try {
      const response = await refetchMetaTagList();
      const originalMetaTagList =
        response?.data?.data.data && response?.data?.data.data.length > 0 ? response.data.data.data : []; // {id:number; name:string; engName:string;}[]
      const flattenedMetaTagList = originalMetaTagList.flatMap((item) => [item.name, item.engName]); // string[]
      const now = Date.now();

      // 로컬 스토리지에 메타태그 데이터와 만료 시간 저장
      localStorage.setItem(
        StorageKeyEnum.ALL_META_TAG_LIST_DATA_KEY,
        JSON.stringify({
          metaTagList: flattenedMetaTagList,
          expiredDate: now + META_TAG_LIST_EXPIRATION_INTERVAL
        })
      );

      return flattenedMetaTagList;
    } catch (error) {
      console.error('Error fetching meta tag list:', error);
      return null;
    }
  };

  const handleMetaTagsUpdate = (tags: string[]) => {
    onSetMetaTag({ selectedMetaTags: tags });
  };

  // 스페이스바 입력 시 태그 추가, handleInputKeywordChange보다 먼저 실행됨
  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case ' ': {
        e.preventDefault(); // 스페이스 입력 방지
        if (tagInputKeyword.trim().length > 0) {
          addTag(tagInputKeyword);
          setIsTagging(true); // 태그 추가 플래그 설정
          setTagAutocompleteKeyword('');
          setTagInputKeyword(''); // 즉시 입력 필드 비우기
        }
        break;
      }
      case 'Enter': {
        // Autocomplete options 목록이 열려있지 않을 때만 handleTagSubmit 실행
        if (tagAutocompleteList.length === 0) {
          e.preventDefault(); // 스페이스 입력 방지
          if (tagInputKeyword.trim().length > 0) {
            addTag(tagInputKeyword);
            setIsTagging(true); // 태그 추가 플래그 설정
            setTagAutocompleteKeyword('');
            setTagInputKeyword(''); // 즉시 입력 필드 비우기
          }
        }
        break;
      }

      default: {
        return;
      }
    }
  };

  const handleTagInputKeywordChange = (event: React.SyntheticEvent, newValue: string) => {
    if (isTagging) {
      setIsTagging(false); // 태그 추가 후 `onInputChange` 무시
      return;
    }

    // `nativeEvent`를 통해 실제 키보드 이벤트 가져오기
    const keyboardEvent = event.nativeEvent as KeyboardEvent;

    //스페이스바 입력 시: 태그 추가 & 입력 필드 초기화
    if (keyboardEvent.key === ' ') {
      event.preventDefault(); // 기본 동작 방지
      if (newValue.trim().length > 0) {
        addTag(newValue);
        setTagInputKeyword(''); // 입력 필드 초기화
      }
      return;
    }

    // 백스페이스 입력 시: 마지막 태그 삭제
    if (keyboardEvent.key === 'Backspace' && newValue === '' && selectedMetaTags.length > 0) {
      handleMetaTagsUpdate(selectedMetaTags.slice(0, -1));
      return;
    }

    // 일반 문자 입력 시: 상태 업데이트
    if (newValue.trim().length > 0) {
      setTagInputKeyword(newValue);
      handleTagAutocompleteKeywordChange(newValue);
    } else {
      setTagInputKeyword('');
      handleTagAutocompleteKeywordChange(newValue);
    }
  };

  // 오토컴플리트 리스트 불러오기에 이용할 키워드 setState
  const handleTagAutocompleteKeywordChange = useCallback(
    debounce((newValue: string) => {
      setTagAutocompleteKeyword(newValue);
    }, 500),
    []
  );

  // 태그 추가 함수
  const addTag = async (tag: string) => {
    const trimmedTag = tag.trim();

    if (!trimmedTag || selectedMetaTags.includes(trimmedTag)) {
      return;
    }

    // 캐시된 메타 태그 전체 리스트를 로드
    const { metaTagList: cachedMetaTagList, expiredDate } = loadCachedMetaTagList();
    const now = Date.now();

    let validMetaTagList = cachedMetaTagList;

    // 캐시가 없거나 만료되었으면 새로 요청
    if (!cachedMetaTagList || !expiredDate || now > expiredDate) {
      validMetaTagList = await fetchMetaTagList();
    }

    // 오토컴플리트 리스트에 trimmedTag가 존재할 시, DB meta_tag 테이블에 존재하는 값이므로 force add 한다
    let needForceAdd = false;
    if (tagAutocompleteList.includes(trimmedTag)) {
      needForceAdd = true;
    }

    // 태그가 유효한지(DB meta_tag 테이블에 존재하는 값인지) 검사 후 추가
    if (needForceAdd || (validMetaTagList && validMetaTagList.some((item) => item === trimmedTag))) {
      setTagAutocompleteKeyword('');
      // debounce된 함수 호출 취소하여 setTagAutocompleteKeyword(value) 재실행 방지
      handleTagAutocompleteKeywordChange.cancel();
      handleMetaTagsUpdate([...selectedMetaTags, trimmedTag]);
    } else {
      console.log('Inexist tag');
    }
  };

  // 오토컴플리트에서 태그 선택 시 추가 (검색어는 초기화)
  const handleTagAutocompleteChange = (event: React.SyntheticEvent, newValue: string | null) => {
    if (newValue && typeof newValue === 'string' && newValue.trim().length > 0) {
      addTag(newValue);
      setTagAutocompleteKeyword('');
      setTagInputKeyword('');

      // debounce된 함수 호출 취소하여 setTagAutocompleteKeyword(value) 재실행 방지
      handleTagAutocompleteKeywordChange.cancel();
    }
  };

  // 단일 태그 삭제
  const handleSingleTagClear = (tagToRemove: string) => {
    handleMetaTagsUpdate(selectedMetaTags.filter((tag) => tag !== tagToRemove));
  };

  // 태그 및 입력된 텍스트 삭제
  const handleTagClear = () => {
    handleMetaTagsUpdate([]);
    onSetMetaTag({ selectedMetaTags: [] });
    setTagAutocompleteKeyword('');
    setTagInputKeyword('');
  };

  // 태그 검색 요청
  const handleTagSubmit = () => {
    onSetMetaTag({ selectedMetaTags: selectedMetaTags.length > 0 ? selectedMetaTags : [] });
  };

  // 글콘티 AI 추천 단일 태그 삭제
  const handleSingleAiRecommendedTagClear = (tagToRemove: string) => {
    const updatedTags = aiRecommendedMetaTags.filter((tag) => tag !== tagToRemove);

    onSetMetaTag({ aiRecommendedMetaTags: updatedTags });
  };

  return {
    // 유저 입력 태그
    selectedMetaTags,
    tagInputKeyword,
    tagAutocompleteList,
    handleTagKeyDown,
    handleTagInputKeywordChange,
    handleTagAutocompleteKeywordChange,
    handleTagAutocompleteChange,
    handleSingleTagClear,
    handleTagClear,
    handleTagSubmit,

    // 글콘티 AI 추천 태그
    aiRecommendedMetaTags,
    handleSingleAiRecommendedTagClear
  };
};
